import propertyTemplateService from "core/services/metaStoreService/property-template-service";

export interface PropertyMapEntry {
    name: string;
    type: string;
    defaultValue: string;
    required: boolean;
    conditionalRequired: string;
    values?: string[];
    group?: string;
}

export interface PropertyTemplate {
    name: string;
    adapterType: string;
    requiresParser: boolean;
    requiresFormatter: boolean;
    propertyMap: PropertyMapEntry[];
}

export const NUMERIC_TYPES = new Set([
    "java.lang.Integer",
    "java.lang.Long",
    "java.lang.Double",
    "java.lang.Float",
    "int",
    "long",
    "double",
    "float"
]);

// Converts the raw JS-template condition string into plain English.
// e.g. "'{{properties.useConnectionProfile}}'==='false'" → "useConnectionProfile is 'false'"
export function humanizeCondition(expr: string): string {
    if (!expr || expr.trim() === "true") return "";
    return expr
        .replace(/'{{properties\.([^}]+)}}'\s*===\s*'([^']+)'/g, "$1 is '$2'")
        .replace(/'{{properties\.([^}]+)}}'\s*!==\s*'([^']+)'/g, "$1 is not '$2'")
        .replace(/'{{properties\.([^}]+)}}'\s*!=\s*'([^']+)'/g, "$1 is not '$2'")
        .replace(/!\s*'{{properties\.([^}]+)}}'\s*\.includes\('([^']+)'\)/g, "$1 does not include '$2'")
        .replace(/'{{properties\.([^}]+)}}'\s*\.includes\('([^']+)'\)/g, "$1 includes '$2'")
        .replace(/&&/g, " and ")
        .replace(/\|\|/g, " or ")
        .replace(/\s+/g, " ")
        .trim();
}

// Escape control characters (newline, carriage return, tab) so a default value
// like the DSVParser row delimiter '\n' is emitted as the literal escape sequence
// instead of breaking the skeleton onto a new line.
function escapeControlChars(value: string): string {
    return value
        .replace(/\n/g, "\\n")
        .replace(/\r/g, "\\r")
        .replace(/\t/g, "\\t");
}

function formatValue(prop: PropertyMapEntry): string {
    const { type, defaultValue, name, values } = prop;

    if (values && values.length > 0) {
        return `'${escapeControlChars(defaultValue || values[0])}'`;
    }

    // Use the mandated `<ALL_CAPS>` placeholder convention (matches buildAdapterBlock
    // and the adapter_properties skill) so the LLM can uncomment-and-fill directly.
    if (!defaultValue) {
        return `'<${name.toUpperCase()}>'`;
    }

    if (type === "java.lang.Boolean" || type === "boolean") {
        return defaultValue;
    }
    if (NUMERIC_TYPES.has(type)) {
        return defaultValue;
    }
    return `'${escapeControlChars(defaultValue)}'`;
}

function buildInlineComment(prop: PropertyMapEntry, leadingParts: string[] = []): string {
    const parts: string[] = [...leadingParts];

    if (prop.values && prop.values.length > 0) {
        parts.push(`Values can be ${prop.values.join(", ")}`);
    }
    if (prop.defaultValue) {
        parts.push(`default: '${escapeControlChars(prop.defaultValue)}'`);
    }
    const readableCondition = humanizeCondition(prop.conditionalRequired);
    if (readableCondition) {
        parts.push(`required when: ${readableCondition}`);
    }

    return parts.length > 0 ? `  -- ${parts.join(" | ")}` : "";
}

// A property belongs to the active scaffold (vs. a commented "optional" line) when
// it is required, conditionally required, has a default, or has enum values.
// Everything else is purely optional and shown commented-out for reference.
function isRequiredField(prop: PropertyMapEntry): boolean {
    return (
        prop.required || !!prop.conditionalRequired || !!prop.defaultValue || !!(prop.values && prop.values.length > 0)
    );
}

// Builds the indented property block (the lines that sit between the USING(...)
// parens) for a single template. Scaffold properties (required, conditionally
// required, defaulted, or enum) become active lines; purely-optional properties
// become commented '-- ... optional' lines. This is the shared engine used for
// both the top-level adapter and, expanded inline, its parser/formatter.
function buildPropertyBlock(template: PropertyTemplate): string {
    // Include every property so the skeleton is the adapter's full field list —
    // it is the single source of truth for an adapter's fields.
    const groupOrder: string[] = [];
    const grouped = new Map<string, PropertyMapEntry[]>();
    for (const prop of template.propertyMap) {
        const group = prop.group || "";
        if (!grouped.has(group)) {
            grouped.set(group, []);
            groupOrder.push(group);
        }
        grouped.get(group)!.push(prop);
    }

    interface PropEntry {
        name: string;
        value: string;
        comment: string;
        groupHeader: string | null;
        optional: boolean;
    }
    const entries: PropEntry[] = [];

    for (const groupName of groupOrder) {
        const props = grouped.get(groupName)!;
        props.forEach((prop, idx) => {
            const optional = !isRequiredField(prop);
            entries.push({
                name: prop.name,
                value: formatValue(prop),
                comment: buildInlineComment(prop, optional ? ["optional"] : []),
                groupHeader: idx === 0 && groupName ? groupName : null,
                optional
            });
        });
    }

    // Active line: a scaffold property — emitted ready to compile (concrete value)
    //   or as a '<PLACEHOLDER>' the LLM fills in.
    // Commented line: a purely-optional property — shown for reference so the
    //   caller can uncomment what they need.
    const isActive = (e: PropEntry) => !e.optional;
    const activeCount = entries.filter(isActive).length;
    let activeIndex = 0;

    const lines: string[] = [];
    entries.forEach(e => {
        if (e.groupHeader) {
            lines.push(`    -- [${e.groupHeader}]`);
        }
        if (isActive(e)) {
            const comma = activeIndex < activeCount - 1 ? "," : "";
            lines.push(`    ${e.name}: ${e.value}${comma}${e.comment}`);
            activeIndex++;
        } else {
            lines.push(`    -- ${e.name}: ${e.value}${e.comment}`);
        }
    });

    return lines.join("\n");
}

export function buildTqlSkeletonFromTemplate(
    template: PropertyTemplate,
    componentType: "SOURCE" | "TARGET",
    inputStream?: string,
    parserOrFormatter?: string,
    parserOrFormatterTemplate?: PropertyTemplate | null
): string {
    const instanceName = `${template.name}_1`;
    const outputStream = `${template.name}_1_Stream`;

    const propsBlock = buildPropertyBlock(template);

    // Build a PARSE USING / FORMAT USING clause. When the parser's/formatter's own
    // template is resolvable, scaffold its properties inline (same format as the
    // main block) so the LLM can discover column/row delimiters, quoting, encoding,
    // etc. Fall back to empty parens when the template can't be resolved.
    const buildSubClause = (keyword: string, subName: string): string => {
        if (parserOrFormatterTemplate && parserOrFormatterTemplate.propertyMap?.length) {
            return `${keyword} USING ${subName} (\n${buildPropertyBlock(parserOrFormatterTemplate)}\n)`;
        }
        return `${keyword} USING ${subName} ()`;
    };

    if (componentType === "SOURCE") {
        const parts = [`CREATE SOURCE ${instanceName} USING ${template.name} (`, propsBlock, `)`];
        if (template.requiresParser) {
            parts.push(buildSubClause("PARSE", parserOrFormatter || "DSVParser"));
        }
        parts.push(`OUTPUT TO ${outputStream};`);
        return parts.join("\n");
    }

    const parts = [`CREATE TARGET ${instanceName} USING ${template.name} (`, propsBlock, `)`];
    if (template.requiresFormatter) {
        parts.push(buildSubClause("FORMAT", parserOrFormatter || "DSVFormatter"));
    }
    parts.push(`INPUT FROM ${inputStream || "<INPUT_STREAM>"};`);
    return parts.join("\n");
}

export const resolveTemplate = (name: string): PropertyTemplate | null => {
    const model =
        propertyTemplateService.getPropertyTemplateById(`Global.PROPERTYTEMPLATE.${name}`) ||
        (propertyTemplateService.propertyTemplates as any).findWhere?.({ name });
    return model ? (model.toJSON() as PropertyTemplate) : null;
};

const hasParserOrFormatter = (componentType: "SOURCE" | "TARGET", template: any) => {
    return (
        (componentType === "SOURCE" && template.requiresParser) ||
        (componentType === "TARGET" && template.requiresFormatter)
    );
};

export function getTqlSkeletonForSourceOrTarget(
    componentName: string,
    componentType: "SOURCE" | "TARGET",
    inputStream?: string,
    parserOrFormatter?: string
): string {
    const template = resolveTemplate(componentName);
    if (!template || template.adapterType === "internal") {
        return JSON.stringify({
            status: "adapter-not-found",
            message:
                `The property template "${componentName}" is not available.` +
                `Call adapterNotFound({ adapterName: "${componentName}" }) now.`
        });
    }

    // When the adapter needs a parser/formatter
    const subTemplate = hasParserOrFormatter(componentType, template)
        ? resolveTemplate(parserOrFormatter || (componentType === "SOURCE" ? "DSVParser" : "DSVFormatter"))
        : null;

    const res = buildTqlSkeletonFromTemplate(template, componentType, inputStream, parserOrFormatter, subTemplate);
    return res;
}
